import { useEffect, useRef, useState } from 'react';
import { RunningShoe } from '../types';

interface SearchAutocompleteProps {
  query: string;
  results: RunningShoe[];
  isLoading: boolean;
  isOpen: boolean;
  onSelect: (shoe: RunningShoe) => void;
  onClose: () => void;
}

const SearchAutocomplete = ({
  query,
  results,
  isLoading,
  isOpen,
  onSelect,
  onClose,
}: SearchAutocompleteProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [results]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          // Only handle Enter if there's a selected item
          // Otherwise, let the input handle it for search
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            e.preventDefault();
            onSelect(results[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, results, selectedIndex, onSelect, onClose]);

  // Don't render if not open
  if (!isOpen) return null;

  // Helper function to format price
  const formatPrice = (price: number) => {
    return `$${price}`;
  };

  // Helper function to highlight matching text
  const highlightMatch = (text: string | undefined | null, query: string) => {
    // Guard against undefined/null text
    if (!text) return '';
    if (!query) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50"
    >
      {/* Loading state */}
      {isLoading && (
        <div className="p-4">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hint for short query */}
      {!isLoading && query.length > 0 && query.length < 2 && (
        <div className="p-4 text-center text-gray-500 text-sm">
          Please enter at least 2 characters to search
        </div>
      )}

      {/* Results */}
      {!isLoading && results.length > 0 && (
        <div className="py-2">
          {results.map((shoe, index) => (
            <button
              key={shoe._id}
              onClick={() => onSelect(shoe)}
              className={`w-full px-4 py-2.5 flex items-center gap-3 hover:bg-orange-50 transition-colors text-left ${
                selectedIndex === index ? 'bg-orange-50' : ''
              }`}
            >
              {/* Shoe info - No image, no gender */}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {highlightMatch(shoe.model || shoe.name || 'Unknown', query)}
                </div>
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span>
                    {shoe.price?.current
                      ? formatPrice(shoe.price.current)
                      : 'N/A'}
                  </span>
                  {shoe.price?.discount && shoe.price.discount > 0 && (
                    <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                      -{shoe.price.discount}%
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading &&
        query.length >= 2 &&
        results.length === 0 && (
          <div className="p-6 text-center">
            <div className="text-gray-500 mb-2">
              No shoes found for "{query}"
            </div>
            <div className="text-sm text-gray-400">
              Try different keywords or browse our recommendations
            </div>
          </div>
        )}
    </div>
  );
};

export default SearchAutocomplete;
